import { useState, useEffect, useRef, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useTagGroups, TagGroup } from './useTagGroups';
import { useUpdateTopicPreferences } from './useUpdateTopicPreferences';
import Toast from '../../components/common/pop-up-message';
import useAuth from '../utils/useAuth';
import { ROUTE_PATH } from '../../routes/route-paths';
import { User, useUser, userQueryKeys } from './useUser';
import { CacheAxiosResponse } from 'axios-cache-interceptor';
import { useHubspotEventMutation } from './useEvent';
import { analytics, EventTypes } from '../../utils/analytics';
import moment from 'moment';
import { useQueryClient } from '@tanstack/react-query';

export const MAX_SELECTIONS = 4;

export type TopicPreferencesMode = 'onboarding' | 'settings';

export interface UseTopicPreferencesOptions {
  mode: TopicPreferencesMode;
  toastNotificationContainerRef?: React.RefObject<HTMLDivElement | null>;
}

export interface UseTopicPreferencesResult {
  topicTagGroups: TagGroup[];
  isLoading: boolean;
  selectedTagGroupIds: number[];
  handleToggleTagGroup: (tagGroupId: number) => void;
  maxSelections: number;
  layout: 'grid' | 'rows';
  // Settings only
  hasChanges?: boolean;
  handleSave?: () => Promise<void>;
  // Onboarding only
  handleSkip?: () => void;
  handleContinue?: () => void;
  // Loading states
  isSaving: boolean;
  isUpdatingUser: boolean;
}

export function useTopicPreferences({
  mode,
  toastNotificationContainerRef,
}: UseTopicPreferencesOptions): UseTopicPreferencesResult {
  const history = useHistory();
  const { user } = useAuth();
  const { updateUser } = useUser();
  const queryClient = useQueryClient();
  const hubspotMutation = useHubspotEventMutation();
  const { data: topicTagGroups, isLoading } = useTagGroups();
  const updateTopicPreferences = useUpdateTopicPreferences();
  const isOnboarding = mode === 'onboarding';

  const currentPreferences = useMemo(() => {
    return user?.caller_role?.topic_preferences?.map((pref) => pref.tag_group_id) || [];
  }, [user?.caller_role?.topic_preferences]);

  const lastSavedPreferencesRef = useRef<number[]>([]);

  const [selectedTagGroupIds, setSelectedTagGroupIds] = useState<number[]>(() => {
    if (isOnboarding) return [];
    return user?.caller_role?.topic_preferences?.map((pref) => pref.tag_group_id) || [];
  });
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (!isOnboarding && user?.caller_role?.topic_preferences !== undefined) {
      const prefs = currentPreferences;
      setSelectedTagGroupIds(prefs);
      lastSavedPreferencesRef.current = [...prefs];
      setHasChanges(false);
    }
  }, [isOnboarding, currentPreferences, user?.caller_role?.topic_preferences]);

  useEffect(() => {
    if (!isOnboarding) {
      const currentSaved = lastSavedPreferencesRef.current;
      const hasChanged = JSON.stringify([...selectedTagGroupIds].sort()) !== JSON.stringify([...currentSaved].sort());
      setHasChanges(hasChanged);
    }
  }, [isOnboarding, selectedTagGroupIds]);

  const toastTarget = isOnboarding ? undefined : toastNotificationContainerRef?.current;

  const handleToggleTagGroup = (tagGroupId: number) => {
    setSelectedTagGroupIds((prev) => {
      if (prev.includes(tagGroupId)) {
        return prev.filter((id) => id !== tagGroupId);
      } else {
        if (prev.length >= MAX_SELECTIONS) {
          Toast.error(`You can select up to ${MAX_SELECTIONS} topics`, undefined, toastTarget);
          return prev;
        }
        return [...prev, tagGroupId];
      }
    });
  };

  const completeOnboardingAndNavigate = (data: CacheAxiosResponse<User, unknown>) => {
    hubspotMutation.mutate({
      event: 'onboarding_completed',
      properties: {
        package_code: user?.caller_role.active_subscription?.package.code!,
      },
    });
    analytics.trackEvent(
      EventTypes.COMPLETED_ONBOARDING,
      user?.client_name,
      data?.data?.gender,
      Math.floor(moment().diff(data?.data?.date_of_birth, 'years') / 10) * 10,
    );
    history.push(ROUTE_PATH.HOME);
  };

  const handleSkip = () => {
    const previousUser = queryClient.getQueryData<User>(userQueryKeys.me);
    if (previousUser) {
      queryClient.setQueryData<User>(userQueryKeys.me, {
        ...previousUser,
        is_partial: false,
      });
    }
    updateUser.mutate(
      { is_partial: false },
      {
        onSuccess: (data: CacheAxiosResponse<User, unknown>) => {
          hubspotMutation.mutate({
            event: 'onboarding_completed',
            properties: {
              package_code: user?.caller_role.active_subscription?.package.code!,
            },
          });
          analytics.trackEvent(
            EventTypes.COMPLETED_ONBOARDING,
            user?.client_name,
            data?.data?.gender,
            Math.floor(moment().diff(data?.data?.date_of_birth, 'years') / 10) * 10,
          );
          history.push(ROUTE_PATH.HOME);
        },
        onError: () => {
          if (previousUser) {
            queryClient.setQueryData(userQueryKeys.me, previousUser);
          }
        },
      },
    );
  };

  const handleContinue = () => {
    if (selectedTagGroupIds.length === 0) {
      Toast.error('Please select at least one topic preference');
      return;
    }
    const preferences = selectedTagGroupIds.map((tagGroupId, index) => ({
      tag_group_id: tagGroupId,
      sort_order: index + 1,
    }));
    updateTopicPreferences.mutate(preferences, {
      onSuccess: () => {
        const previousUser = queryClient.getQueryData<User>(userQueryKeys.me);
        if (previousUser) {
          queryClient.setQueryData<User>(userQueryKeys.me, {
            ...previousUser,
            is_partial: false,
          });
        }
        updateUser.mutate(
          { is_partial: false },
          {
            onSuccess: completeOnboardingAndNavigate,
            onError: () => {
              if (previousUser) {
                queryClient.setQueryData(userQueryKeys.me, previousUser);
              }
            },
          },
        );
      },
      onError: (error: unknown) => {
        const err = error as { response?: { data?: { description?: string } } };
        Toast.error(err?.response?.data?.description || 'Failed to save topic preferences');
      },
    });
  };

  const handleSave = async () => {
    const preferences = selectedTagGroupIds.map((tagGroupId, index) => ({
      tag_group_id: tagGroupId,
      sort_order: index + 1,
    }));
    try {
      await updateTopicPreferences.mutateAsync(preferences);
      Toast.success('Changes saved', undefined, toastTarget);
      lastSavedPreferencesRef.current = [...selectedTagGroupIds];
      setHasChanges(false);
      setTimeout(() => history.push(ROUTE_PATH.HOME), 1500);
    } catch {
      // Error handling is done by the mutation
    }
  };

  const result: UseTopicPreferencesResult = {
    topicTagGroups: topicTagGroups ?? [],
    isLoading,
    selectedTagGroupIds,
    handleToggleTagGroup,
    maxSelections: MAX_SELECTIONS,
    layout: isOnboarding ? 'rows' : 'grid',
    isSaving: updateTopicPreferences.isLoading,
    isUpdatingUser: updateUser.isLoading,
  };

  if (isOnboarding) {
    result.handleSkip = handleSkip;
    result.handleContinue = handleContinue;
  } else {
    result.hasChanges = hasChanges;
    result.handleSave = handleSave;
  }

  return result;
}
